import { useState } from 'react';
import { uiFormat, uiText } from '../../i18n';
import { platform } from '../../platform';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { record, rows, text } from '../studio/studio-content';
import { openStudioTarget } from '../studio/studio-navigation';
import { studioDeliveryOffText, studioDeliveryView } from '../studio/studio-order-tail-model';

/** Хвост «Создать кавер» (APP-SPEC-5 §9 «Хвост», §16): этапы, два варианта с плеером (нажатие выбирает вариант),
    «Скачать» выбранного, «Ещё вариант», «Что дальше», «Открыть в чате»; отказ проверки прав — «Заменить песню». */
export function CoverOrderResult({ state }: { state: CircleFormState }) {
  const [picked, setPicked] = useState<number | null>(null);
  const { order, orderId, busy, submissionBlocked } = state;
  if (!orderId) return null;
  const status = order?.status;
  const steps = rows(record(order).steps);
  const variants = (order?.result?.assets ?? []).filter(asset => asset.mime.startsWith('audio/'));
  const captions = rows(record(order?.result?.data).items);
  const moderation = /moderation|rights|rejected/.test(order?.error_code ?? '') || record(record(order).error).kind === 'moderation';
  const amount = order?.financial?.amount_tokens;
  const back = async () => {
    if (await state.returnToForm()) document.dispatchEvent(new CustomEvent('steos:studio:order-clear', { detail: { id: orderId } }));
  };
  return (
    <section className="voice-order cv-res" aria-live="polite">
      <h3>
        {uiText(
          status === 'done'
            ? 'Кавер готов'
            : status === 'failed'
              ? moderation
                ? 'Песня не прошла проверку'
                : 'Кавер не создан'
              : status === 'uncertain'
                ? 'Уточняем результат'
                : 'Делаем кавер',
        )}
      </h3>
      {(status === 'queued' || status === 'running' || !status) && (
        <>
          <p>{uiText('Можно уйти с экрана: бот напишет, когда кавер будет готов.')}</p>
          {steps.length > 0 && (
            <ol className="cv-steps">
              {steps.map((step, i) => (
                <li key={text(step.key) || i} className={text(step.status)}>
                  {text(step.status) === 'done' ? text(step.title_done) || text(step.title) : text(step.title)}
                </li>
              ))}
            </ol>
          )}
        </>
      )}
      <p>{uiFormat('Заказ №{0}', orderId)}</p>
      {status === 'uncertain' && <p>{uiText('Результат пока не подтверждён. Проверьте этот заказ, прежде чем создавать новый.')}</p>}
      {status === 'done' && (
        <p>
          {uiText(
            /* бот сам не присылает (настройки, Q-221) — не обещаем чат */
            studioDeliveryOffText(studioDeliveryView(order)) ??
              (['sent', 'delivered'].includes(String(record(record(order).delivery).state))
                ? 'Мелодия ваша, звучание новое. Бот уже прислал оба варианта в чат, в отдельную тему.'
                : 'Мелодия ваша, звучание новое. Бот пришлёт оба варианта в чат, в отдельную тему.'),
          )}
        </p>
      )}
      {status === 'failed' && (
        <p>
          {uiText(
            moderation
              ? uiFormat('Похоже на чужую запись: каверы делаем только на свои песни. {0}кавер не создан.', amount ? uiFormat('{0} C списаны, ', amount) : '')
              : order?.financial?.state === 'refunded'
                ? 'Сбой на нашей стороне. Circle вернулись на баланс.'
                : 'Сбой на нашей стороне. Попробуйте ещё раз.',
          )}
        </p>
      )}
      <button type="button" disabled={busy || submissionBlocked} onClick={() => void state.refreshStatus()}>
        {uiText('Проверить статус заказа')}
      </button>
      {status === 'done' && !order?.result?.available && <p>{uiText('Результат недоступен. Проверьте историю заказа.')}</p>}
      {status === 'done' && order?.result?.available && (
        <div className="cv-vars" role="radiogroup" aria-label={uiText('Варианты')}>
          {variants.map((asset, i) => {
            const caption = captions[i] ? [text(captions[i].caption), text(captions[i].sub)].filter(Boolean).join(' · ') : '';
            return (
              <button
                key={asset.index}
                type="button"
                role="radio"
                aria-checked={picked === asset.index}
                className={`cv-var${picked === asset.index ? ' on' : ''}`}
                disabled={busy}
                onClick={() => {
                  setPicked(asset.index);
                  void state.openResult(asset.index);
                }}
              >
                <b>{uiFormat('Вариант {0}', i + 1)}</b>
                {caption && <small>{caption}</small>}
              </button>
            );
          })}
        </div>
      )}
      {state.result && state.result.mime.startsWith('audio/') && (
        <div className="cv-player">
          <audio src={state.result.url} controls preload="metadata" aria-label={uiText('Выбранный вариант')} />
          <a href={state.result.url} download={state.result.name}>
            {uiText('Скачать')}
          </a>
        </div>
      )}
      {status === 'done' && (
        <div className="cv-acts">
          <button type="button" disabled={busy || submissionBlocked} onClick={() => void back()}>
            {uiText('Ещё вариант')}
          </button>
          <h4>{uiText('Что дальше')}</h4>
          <button type="button" disabled={busy || submissionBlocked} onClick={() => void back()}>
            {uiText('Изменить стиль')}
          </button>
          <button type="button" onClick={() => openStudioTarget({ screen: 'circle', who: 'audio' })}>
            {uiText('Сделать кружок')}
          </button>
          {platform.kind === 'telegram' && (
            <button type="button" className="btn" onClick={() => platform.close()}>
              {uiText('Открыть в чате')}
            </button>
          )}
        </div>
      )}
      {status === 'failed' && (
        <div className="cv-acts">
          <button type="button" className="btn" disabled={busy || submissionBlocked} onClick={() => void back()}>
            {uiText(moderation ? 'Заменить песню' : 'Попробовать ещё раз')}
          </button>
          {!moderation && (
            <button type="button" className="lnk" disabled={busy || submissionBlocked} onClick={() => void back()}>
              {uiText('К настройкам')}
            </button>
          )}
        </div>
      )}
      {order?.financial?.state === 'dev_free' && <p>{uiText('Тестовый заказ без списания')}</p>}
      {order?.financial?.state === 'refunded' && <p>{uiText('Списание возвращено')}</p>}
      {amount && status === 'done' && !['dev_free', 'refunded'].includes(order?.financial?.state ?? '') && <p className="mono">{uiFormat('Списано {0} C', amount)}</p>}
    </section>
  );
}
